import { useEffect, useRef, useState } from 'react';

interface UseRevealOptions {
  /** Fraction of the element that must be visible to trigger. */
  threshold?: number;
  /** Margin around the root for early/late triggering. */
  rootMargin?: string;
  /** When false, toggle off when the element leaves the viewport. */
  once?: boolean;
}

/**
 * IntersectionObserver-based reveal. Returns a ref to attach and a boolean
 * for the in-view state. Pair with the `.psl-reveal` CSS in tokens.css —
 * add `is-in-view` to the same element when revealed.
 *
 * Defaults: threshold 0.15, rootMargin '0px 0px -10% 0px' so the reveal
 * fires a touch before the element fully enters the viewport, and once=true
 * so it doesn't replay on scrolling back.
 *
 * Falls back to immediately-revealed when IntersectionObserver isn't
 * available (very old browsers, SSR, jsdom in tests).
 */
export function useReveal<T extends HTMLElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
  once = true,
}: UseRevealOptions = {}) {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        // An element taller than the viewport can never reach a fractional
        // threshold (e.g. 0.15 needs 15% on screen at once), so on mobile —
        // where long sections collapse to a single tall column — it would
        // stay hidden forever. Treat "taller than the root and at least
        // partly visible" as revealed too, while keeping the threshold feel
        // for normal-height sections.
        const root = entry.rootBounds;
        const tallerThanRoot =
          root != null && entry.boundingClientRect.height > root.height;
        const meetsThreshold = entry.intersectionRatio >= threshold;

        if (entry.isIntersecting && (meetsThreshold || tallerThanRoot)) {
          setRevealed(true);
          if (once) io.disconnect();
        } else if (!once && !entry.isIntersecting) {
          setRevealed(false);
        }
      },
      // Observe the 0 crossing too, so the tall-element fallback above can
      // fire even when the configured threshold is never reached.
      { threshold: [0, threshold], rootMargin },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, revealed };
}
